/**
 * ERP shell route — Manager+ everywhere except /erp/dispatches/*
 * (Apr-2026 ERP v1 PR-A; sales-allowed dispatch slice landed in PR-G).
 *
 * The ERP module runs alongside the CRM in the same app shell. The
 * top-level brand label switches to "ManufactureERP" while the user
 * is under any /erp/* route, and the nav swaps to ERP entries.
 *
 * Access (PR-G):
 *   * Manager / admin: every /erp/* route.
 *   * Sales: only the dispatch surface (`/erp/dispatches`,
 *     `/erp/dispatches/new`, `/erp/dispatches/{id}`,
 *     `/erp/dispatches/{id}/deliveries`). Hitting any other /erp/*
 *     redirects them back to /deals — the API would 403 anyway.
 *
 * The "is this a sales-allowed sub-tree" check is a path-prefix test
 * here in the shell's ``beforeLoad`` rather than per-child guards
 * because (a) it keeps every existing manager-only ERP child route
 * untouched and (b) the prefix list is short and obviously
 * exhaustive (just the dispatch tree today, anonymized prints
 * tomorrow). PR-H will add /erp/dispatches/{id}/print/field to the
 * sales-allowed prefix list.
 */

import { Outlet, createFileRoute, redirect } from "@tanstack/react-router";

import { useAuthStore } from "../lib/auth";

const SALES_ALLOWED_ERP_PATH_PREFIXES = ["/erp/dispatches", "/erp/orders"] as const;

function isSalesAllowedErpPath(pathname: string): boolean {
  return SALES_ALLOWED_ERP_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

export const Route = createFileRoute("/_app/erp")({
  beforeLoad: ({ location }) => {
    const user = useAuthStore.getState().user;
    if (!user) return;
    if (user.role === "admin" || user.role === "manager" || user.role === "production") return;
    if (isSalesAllowedErpPath(location.pathname)) return;
    // Redirect to the sales landing without a stray ?redirect param
    // (keeps the URL clean and consistent with the /team, /reports and
    // /settings guards, which the server enforces regardless).
    throw redirect({ to: "/deals" });
  },
  component: ErpShell,
});

function ErpShell() {
  return (
    <div className="min-h-[calc(100vh-3.5rem)]">
      <Outlet />
    </div>
  );
}
